import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  QrCode, Bot, Phone, Mail, MessageSquare, Repeat, Tv, Radio, Target,
  Mail as MailIcon, Megaphone, ShieldCheck, Send, Sparkles, Crown, MapPin,
  CheckCircle2, X, ArrowRight, Building2, Award, Zap, BarChart3, Globe,
  Smartphone, Voicemail, Calendar, Star, Trophy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SeoMeta from "@/components/SeoMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "/assets/postcard-talks-back-hero.png";
const SECONDARY_IMG = "/assets/onlybest_hero.png";
const ECOSYSTEM_IMG = "/assets/best-of-best-partner-kit.png";
const VERIFIED_BADGE = "/assets/localbest-verified-badge.png";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function PostcardThatTalksBackPage() {
  return (
    <div className="min-h-screen bg-[#040814] text-white" data-testid="postcard-talks-back-page">
      <SeoMeta
        title="The Postcard That Talks Back™ | AI-Powered Direct Mail by LocalBest.ai"
        description="Launch an AI-powered postcard campaign that connects direct mail to QR codes, verified LocalBest listings, AI assistants, AI Voice Agents, CRM follow-up, and omnichannel retargeting."
        canonical="https://localbest.ai/postcard-that-talks-back"
        ogTitle="The Postcard That Talks Back™ — Powered by LocalBest.ai"
        ogDescription="Turn a printed postcard into a live local lead funnel with AI assistants, QR codes, voice agents, CRM, and omnichannel retargeting."
        ogImage="https://localbest.ai/assets/postcard-talks-back-hero.png"
      />

      <Hero />
      <WhatIs />
      <HowItWorks />
      <Comparison />
      <WhatComes />
      <PerfectFor />
      <Targeting />
      <Omnichannel />
      <Outcomes />
      <ExampleCampaign />
      <BestOfBestAdvantage />
      <WhyNow />
      <FinalCTA />
      <InquiryForm />
    </div>
  );
}

/* ===========================  HERO  =========================== */
function Hero() {
  return (
    <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28" data-testid="postcard-hero">
      {/* Background glow */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1100px] h-[600px] bg-[#00A3FF]/12 blur-[140px] rounded-full" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-[#F4B41A]/10 blur-[120px] rounded-full" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        {/* Copy */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="lg:col-span-5 order-2 lg:order-1"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#F4B41A]/30 bg-[#F4B41A]/10 text-[#F4B41A] text-xs font-semibold tracking-wider uppercase mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            New • Print-to-AI Direct Mail
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] font-['Outfit'] tracking-tight">
            The Postcard
            <br />
            <span className="bg-gradient-to-r from-[#F4B41A] via-[#FFD66B] to-[#F4B41A] bg-clip-text text-transparent">
              That Talks Back
            </span>
            <span className="text-[#F4B41A] text-3xl align-super">™</span>
          </h1>

          <p className="mt-6 text-xl text-[#00A3FF] font-medium">
            The AI-powered direct mail campaign that turns a printed postcard into a live local lead funnel.
          </p>

          <div className="mt-6 space-y-4 text-[#C7D2E0] leading-relaxed">
            <p>Most postcards get looked at once and thrown away.</p>
            <p className="text-white font-semibold text-lg">This one talks back.</p>
            <p>
              With LocalBest.ai, your postcard can send people to your verified listing, launch an AI
              assistant, unlock offers, let customers call an AI Voice Agent, capture leads, and
              trigger follow-up across email, SMS, ringless voicemail, social media, streaming TV,
              radio, and the web.
            </p>
            <p className="text-white">
              This is not just direct mail.
              <br />
              <span className="text-[#F4B41A] font-semibold">
                This is print advertising connected to AI, automation, retargeting, and real customer action.
              </span>
            </p>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a href="#inquiry-form">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-gradient-to-r from-[#F4B41A] to-[#E0A015] hover:from-[#FFD66B] hover:to-[#F4B41A] text-[#0A0F1C] font-bold rounded-full px-7 shadow-[0_0_30px_rgba(244,180,26,0.35)]"
                data-testid="hero-primary-cta"
              >
                Launch My Talking Postcard Campaign
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
            <a href="#how-it-works">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full px-7"
                data-testid="hero-secondary-cta"
              >
                See How It Works
              </Button>
            </a>
          </div>
        </motion.div>

        {/* Hero image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="lg:col-span-7 order-1 lg:order-2 relative"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,163,255,0.4)] border border-white/10">
            <div className="absolute -inset-1 bg-gradient-to-br from-[#F4B41A]/40 via-[#00A3FF]/30 to-transparent rounded-2xl blur-xl opacity-70 -z-10" />
            <img
              src={HERO_IMG}
              alt="The Postcard That Talks Back — Mike Walker of GFS Home Loans AI-powered LocalBest postcard demo"
              className="w-full h-auto block"
              data-testid="hero-postcard-image"
            />
          </div>
          <p className="mt-4 text-center text-sm text-[#94A3B8] italic">
            Example: Mike Walker of GFS Home Loans — AI-powered LocalBest postcard demo.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ===========================  SECTION 1: What is  =========================== */
function WhatIs() {
  return (
    <Section id="what-is">
      <SectionHeader
        eyebrow="What it is"
        title="A Postcard That Does More Than Sit in the Mailbox"
      />
      <div className="grid lg:grid-cols-2 gap-10 items-start mt-8">
        <div className="space-y-5 text-[#C7D2E0] leading-relaxed">
          <p>
            The Postcard That Talks Back™ is a premium LocalBest.ai direct mail campaign that
            connects a physical postcard to an interactive digital experience.
          </p>
          <p>
            When someone receives the postcard, they can scan the QR code, visit the business's
            verified LocalBest page, claim offers, ask the AI assistant questions, call the AI
            Voice Agent, request help, book an appointment, or get connected with the business
            instantly.
          </p>
          <p className="text-white font-medium">
            Instead of sending a basic postcard and hoping someone calls, we create a full
            print-to-digital customer journey.
          </p>
        </div>
        <ol className="space-y-3">
          {[
            "The customer receives the postcard.",
            "They scan, call, or visit.",
            "They engage with the AI assistant or offer.",
            "The lead is captured in the CRM.",
            "Follow-up begins automatically.",
            "Retargeting ads follow them online, on social media, streaming TV, and audio.",
            "Your business keeps showing up until they are ready to act.",
          ].map((step, i) => (
            <li
              key={i}
              className="flex gap-4 items-start p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:border-[#00A3FF]/40 transition-colors"
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-[#00A3FF] to-[#0066CC] flex items-center justify-center text-white text-sm font-bold">
                {i + 1}
              </div>
              <span className="text-[#E0E7F0] pt-0.5">{step}</span>
            </li>
          ))}
        </ol>
      </div>
    </Section>
  );
}

/* ===========================  SECTION 2: How It Works  =========================== */
function HowItWorks() {
  const steps = [
    {
      icon: Megaphone,
      title: "We Create the Campaign",
      desc: "We design a premium postcard around your business, your offer, your verified LocalBest listing, your AI assistant, and your main call-to-action.",
    },
    {
      icon: Target,
      title: "We Target the Right Audience",
      desc: "Mail to targeted homeowners, buyers, neighborhoods, zip codes, carrier routes, new movers, high-income households, business owners, or other custom audiences.",
    },
    {
      icon: QrCode,
      title: "The Customer Scans or Calls",
      desc: "The postcard includes a QR code and/or AI Voice Agent phone number so people can instantly interact with your business from their phone.",
    },
    {
      icon: Bot,
      title: "AI Engages and Captures the Lead",
      desc: "Your AI assistant answers questions, explains offers, guides the customer, collects information, and moves the prospect to the next step.",
    },
    {
      icon: Repeat,
      title: "We Follow Up Everywhere",
      desc: "Continue through email, SMS, ringless voicemail, CRM automation, social retargeting, Google retargeting, streaming TV, radio/audio, and follow-up postcards.",
    },
  ];

  return (
    <Section id="how-it-works" tone="darker">
      <SectionHeader eyebrow="How it works" title="How The Postcard Talks Back" />
      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mt-10">
        {steps.map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ delay: i * 0.08 }}
            className="relative rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.01] border border-white/10 p-6 hover:border-[#F4B41A]/40 hover:-translate-y-1 transition-all"
            data-testid={`how-step-${i + 1}`}
          >
            <div className="absolute -top-3 -left-3 w-10 h-10 rounded-xl bg-gradient-to-br from-[#F4B41A] to-[#C18B0E] text-[#0A0F1C] font-bold flex items-center justify-center shadow-lg">
              {i + 1}
            </div>
            <s.icon className="w-7 h-7 text-[#00A3FF] mb-3" />
            <h3 className="text-white font-semibold text-base mb-2">{s.title}</h3>
            <p className="text-[#94A3B8] text-sm leading-relaxed">{s.desc}</p>
          </motion.div>
        ))}
      </div>

      {/* Flow arrow row (desktop) */}
      <div className="hidden lg:flex items-center justify-center gap-2 mt-10 text-xs uppercase tracking-widest text-[#64748B] font-semibold">
        <span>Print</span> <ArrowRight className="w-3 h-3" />
        <span>Scan</span> <ArrowRight className="w-3 h-3" />
        <span className="text-[#F4B41A]">AI</span> <ArrowRight className="w-3 h-3" />
        <span>Lead</span> <ArrowRight className="w-3 h-3" />
        <span>Follow-Up</span> <ArrowRight className="w-3 h-3" />
        <span>Retargeting</span>
      </div>
    </Section>
  );
}

/* ===========================  SECTION 3: Comparison  =========================== */
function Comparison() {
  const normal = [
    "One-time impression",
    "No AI assistant",
    "No live conversation",
    "No verified listing",
    "No retargeting",
    "No CRM automation",
    "No measurable customer journey",
    "Easy to ignore",
    "Hard to track",
  ];
  const talkback = [
    "Premium branded design",
    "Verified LocalBest listing",
    "QR code to offers and information",
    "AI assistant answers questions",
    "AI Voice Agent can receive calls",
    "CRM lead capture",
    "Email, SMS, and RVM follow-up",
    "Retargeting online, on TV, and audio",
    "Measurable scans, clicks, calls, and leads",
    "Built to create action",
  ];

  return (
    <Section id="comparison">
      <SectionHeader
        eyebrow="What makes it different"
        title="This Is Not a Coupon Mailer. This Is an AI-Powered Local Lead Engine."
      />
      <div className="grid md:grid-cols-2 gap-6 mt-10">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-7" data-testid="compare-normal">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-[#1E293B] flex items-center justify-center">
              <MailIcon className="w-5 h-5 text-[#64748B]" />
            </div>
            <h3 className="text-xl font-semibold text-[#94A3B8]">Normal Postcard</h3>
          </div>
          <ul className="space-y-2.5">
            {normal.map((n, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[#94A3B8]">
                <X className="w-4 h-4 mt-0.5 text-red-400/70 flex-shrink-0" />
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </div>

        <div
          className="rounded-2xl border border-[#F4B41A]/40 bg-gradient-to-br from-[#F4B41A]/[0.08] via-[#00A3FF]/[0.05] to-transparent p-7 shadow-[0_0_60px_-20px_rgba(244,180,26,0.4)]"
          data-testid="compare-talkback"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#F4B41A] to-[#C18B0E] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#0A0F1C]" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">LocalBest Talking Postcard</h3>
              <p className="text-xs text-[#F4B41A] uppercase tracking-wider font-semibold">
                Powered by LocalBest.ai
              </p>
            </div>
          </div>
          <ul className="space-y-2.5">
            {talkback.map((t, i) => (
              <li key={i} className="flex items-start gap-2.5 text-white">
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#F4B41A] flex-shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
}

/* ===========================  SECTION 4: What Comes With  =========================== */
function WhatComes() {
  const features = [
    { icon: Megaphone, t: "Premium Postcard Design", d: "Custom campaign design with your brand, offer, listing, QR code, and call-to-action." },
    { icon: ShieldCheck, t: "Verified LocalBest Listing", d: "Traffic flows to a trusted LocalBest.ai page showcasing your brand, offers, videos, photos, reviews, and CTAs." },
    { icon: Bot, t: "AI Assistant Experience", d: "Customers ask questions, explore offers, and take the next step — no waiting for a human." },
    { icon: Phone, t: "AI Voice Agent Option", d: "Add a phone number customers can call to interact with an AI-powered voice assistant 24/7." },
    { icon: QrCode, t: "QR Code Tracking", d: "Track scans, visits, offers claimed, and customer engagement from your campaign." },
    { icon: Target, t: "Targeted Mailing Lists", d: "Reach homeowners, specific zip codes, neighborhoods, new movers, business owners, or custom audiences." },
    { icon: BarChart3, t: "CRM Lead Capture", d: "Leads are routed into the CRM for follow-up, pipeline tracking, appointments, and sales notifications." },
    { icon: Send, t: "Email, SMS, and RVM Follow-Up", d: "Continue the conversation after the first scan, call, or form submission." },
    { icon: Repeat, t: "Retargeting Ads", d: "Keep showing up on Facebook, Instagram, Google, YouTube, TikTok, streaming TV, audio/radio, and beyond." },
    { icon: Award, t: "Offer & Incentive Strategy", d: "Rewards, coupons, homeowner incentives, free inspections, consultations, and limited-time promotions." },
    { icon: BarChart3, t: "Campaign Reporting", d: "Scans, visits, calls, leads, form submissions, offer claims, and full campaign activity." },
    { icon: Crown, t: "Best of Best Boost", d: "Pair with a Best of Best listing for premium positioning, badges, and category authority." },
  ];

  return (
    <Section id="features" tone="darker">
      <SectionHeader
        eyebrow="The campaign stack"
        title="Everything Needed to Turn Direct Mail Into Digital Momentum"
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
        {features.map((f, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: i * 0.04 }}
            className="group rounded-xl p-5 bg-white/[0.03] border border-white/10 hover:border-[#00A3FF]/50 hover:bg-white/[0.06] transition-all"
            data-testid={`feature-${i}`}
          >
            <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#00A3FF]/20 to-[#0066CC]/10 border border-[#00A3FF]/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <f.icon className="w-5 h-5 text-[#00A3FF]" />
            </div>
            <h3 className="font-semibold text-white mb-1.5">{f.t}</h3>
            <p className="text-sm text-[#94A3B8] leading-relaxed">{f.d}</p>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ===========================  SECTION 5: Perfect For  =========================== */
function PerfectFor() {
  const industries = [
    "Roofing companies", "HVAC companies", "Plumbers", "Remodelers",
    "Mortgage loan officers", "Realtors", "Insurance agencies", "Restaurants",
    "Coffee shops", "Med spas", "Dental offices", "Auto dealers",
    "Home service companies", "Local professionals", "Best of Best winners",
    "Sponsored LocalBest listings", "Dispatch team partners",
  ];

  return (
    <Section id="industries">
      <SectionHeader
        eyebrow="Who it's for"
        title="Perfect for Businesses That Want to Be Seen, Remembered, and Chosen"
      />
      <div className="flex flex-wrap gap-2.5 mt-10 justify-center">
        {industries.map((i, idx) => (
          <span
            key={idx}
            className="px-4 py-2 rounded-full bg-white/[0.04] border border-white/10 text-[#E0E7F0] text-sm hover:border-[#F4B41A]/40 hover:bg-[#F4B41A]/5 transition-colors"
          >
            {i}
          </span>
        ))}
      </div>
      <p className="mt-10 text-center text-lg text-[#C7D2E0] max-w-3xl mx-auto leading-relaxed">
        If your business depends on local trust, local visibility, and local action,{" "}
        <span className="text-white font-semibold">
          this campaign gives people a reason to notice you, scan you, call you, and remember you.
        </span>
      </p>
    </Section>
  );
}

/* ===========================  SECTION 6: Targeting  =========================== */
function Targeting() {
  return (
    <Section id="targeting" tone="darker">
      <SectionHeader
        eyebrow="Mailing strategy"
        title="Mail to the Right People, Not Just Random Mailboxes"
      />
      <div className="grid md:grid-cols-2 gap-6 mt-10">
        <div className="rounded-2xl p-7 border border-white/10 bg-white/[0.03]">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-[#00A3FF]/15 border border-[#00A3FF]/30 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-[#00A3FF]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-[#00A3FF] font-semibold">Option 1</p>
              <h3 className="text-xl font-bold text-white">Saturation Mailing</h3>
            </div>
          </div>
          <p className="text-[#C7D2E0] mb-4">
            Best for brand awareness, community launches, local deals, Best of Best announcements, and broad neighborhood exposure.
          </p>
          <ul className="space-y-2 text-sm text-[#E0E7F0]">
            {[
              "Mail to entire neighborhoods",
              "Mail to selected carrier routes",
              "Mail to full zip codes",
              "Promote a local Best of Best winner",
              "Launch a LocalBest campaign in a community",
            ].map((u, i) => (
              <li key={i} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#00A3FF] mt-0.5 flex-shrink-0" />{u}</li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl p-7 border border-[#F4B41A]/30 bg-gradient-to-br from-[#F4B41A]/[0.06] to-transparent">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-[#F4B41A]/15 border border-[#F4B41A]/30 flex items-center justify-center">
              <Target className="w-5 h-5 text-[#F4B41A]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-[#F4B41A] font-semibold">Option 2</p>
              <h3 className="text-xl font-bold text-white">Precision Targeted Mailing</h3>
            </div>
          </div>
          <p className="text-[#C7D2E0] mb-4">
            Best for high-intent services and specific customer types.
          </p>
          <ul className="grid grid-cols-2 gap-2 text-sm text-[#E0E7F0]">
            {[
              "Homeowners", "Owner-occupied homes", "Specific zip codes", "Homes above a value",
              "Homes built before a year", "New movers", "Likely buyers", "High-income households",
              "Business owners", "Local neighborhoods", "Radius around a business", "Custom audiences",
            ].map((u, i) => (
              <li key={i} className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-[#F4B41A] mt-1 flex-shrink-0" />{u}</li>
            ))}
          </ul>
        </div>
      </div>
      <p className="mt-8 text-[#C7D2E0] text-center max-w-4xl mx-auto leading-relaxed">
        Example: a roofing company can target homeowners in specific neighborhoods. A mortgage loan officer
        can target homebuyers and referral audiences. A restaurant can target nearby households with an offer.
        A home service company can focus on older homes that may need upgrades or repairs.
      </p>
    </Section>
  );
}

/* ===========================  SECTION 7: Omnichannel  =========================== */
function Omnichannel() {
  const channels = [
    { icon: MailIcon, t: "Postcard arrives in the mailbox" },
    { icon: QrCode, t: "QR code scan or AI Voice call" },
    { icon: ShieldCheck, t: "Verified LocalBest listing visit" },
    { icon: Bot, t: "AI assistant conversation" },
    { icon: Award, t: "Offer claim or appointment request" },
    { icon: BarChart3, t: "CRM lead capture" },
    { icon: Mail, t: "Email follow-up" },
    { icon: MessageSquare, t: "SMS follow-up" },
    { icon: Voicemail, t: "Ringless voicemail" },
    { icon: Repeat, t: "Retargeting ads" },
    { icon: Tv, t: "Streaming TV visibility" },
    { icon: Radio, t: "Audio / radio retargeting" },
    { icon: MailIcon, t: "Additional postcard follow-up" },
    { icon: Send, t: "Sales team notification" },
  ];
  return (
    <Section id="omnichannel">
      <SectionHeader
        eyebrow="Omnichannel follow-up"
        title="The Postcard Is Only the Beginning"
      />
      <p className="text-[#C7D2E0] text-center max-w-3xl mx-auto -mt-2 mb-10 leading-relaxed">
        The real power happens <em>after</em> someone interacts. Once a prospect scans, calls, clicks,
        claims an offer, or visits the listing, your LocalBest campaign continues showing up across
        every channel they spend time on.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {channels.map((c, i) => (
          <div
            key={i}
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10 hover:border-[#00A3FF]/40 transition-colors"
          >
            <c.icon className="w-5 h-5 text-[#00A3FF] flex-shrink-0" />
            <span className="text-[#E0E7F0] text-sm">{c.t}</span>
          </div>
        ))}
      </div>
      <p className="mt-10 text-center text-xl font-semibold text-white max-w-2xl mx-auto">
        We do not just mail and hope.{" "}
        <span className="text-[#F4B41A]">We mail, track, engage, follow up, and retarget.</span>
      </p>
    </Section>
  );
}

/* ===========================  SECTION 8: Outcomes  =========================== */
function Outcomes() {
  const outs = [
    "More listing visits", "More offer claims", "More calls", "More AI conversations",
    "More qualified leads", "More appointments", "More repeat visibility",
    "More retargeting audiences", "More brand trust", "More local authority",
    "More measurable campaign data", "More reasons for customers to choose you",
  ];
  return (
    <Section id="outcomes" tone="darker">
      <SectionHeader eyebrow="Outcomes" title="Built to Create Real Local Action" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-10">
        {outs.map((o, i) => (
          <div
            key={i}
            className="flex items-center gap-3 px-5 py-4 rounded-xl bg-gradient-to-br from-[#F4B41A]/[0.06] to-transparent border border-[#F4B41A]/20"
          >
            <TrendingUpIcon />
            <span className="text-white font-medium">{o}</span>
          </div>
        ))}
      </div>
      <p className="mt-10 text-center text-[#C7D2E0] max-w-3xl mx-auto leading-relaxed">
        The goal is not just to get your postcard <em>seen</em>. The goal is to turn{" "}
        <span className="text-white">attention into interaction</span>, interaction into{" "}
        <span className="text-white">leads</span>, and leads into{" "}
        <span className="text-[#F4B41A] font-semibold">booked opportunities.</span>
      </p>
    </Section>
  );
}

function TrendingUpIcon() {
  return (
    <div className="w-9 h-9 rounded-lg bg-[#F4B41A]/15 border border-[#F4B41A]/30 flex items-center justify-center flex-shrink-0">
      <Zap className="w-4 h-4 text-[#F4B41A]" />
    </div>
  );
}

/* ===========================  SECTION 9: Example Campaign  =========================== */
function ExampleCampaign() {
  return (
    <Section id="example">
      <SectionHeader eyebrow="Example campaign" title="Example: Mike Walker of GFS Home Loans" />
      <div className="grid lg:grid-cols-12 gap-10 items-center mt-10">
        <div className="lg:col-span-6 rounded-2xl overflow-hidden border border-white/10 shadow-[0_20px_60px_-20px_rgba(0,163,255,0.35)]">
          <img src={HERO_IMG} alt="Mike Walker postcard example" className="w-full h-auto" />
        </div>
        <div className="lg:col-span-6 space-y-5 text-[#C7D2E0] leading-relaxed">
          <p>
            In this example, the postcard promotes <span className="text-white font-semibold">Mike Walker of GFS Home Loans</span>{" "}
            with a verified LocalBest page, QR code, AI Mortgage Assistant, AI Voice Agent CTA,
            homebuyer rewards, and mortgage guidance.
          </p>
          <p>
            A prospect can receive the postcard, scan the QR code, visit Mike's verified page, ask
            mortgage questions, explore loan options, call the AI Voice Agent, and take the next
            step toward pre-approval or a conversation with Mike.
          </p>
          <p className="text-white font-medium">
            This same campaign structure can be adapted for almost any local business category.
          </p>
          <div className="flex flex-wrap gap-2 pt-2">
            {["Verified Page", "QR Code", "AI Assistant", "Voice Agent", "Rewards Offer", "Retargeting"].map((c) => (
              <span key={c} className="px-3 py-1.5 rounded-full bg-[#00A3FF]/10 border border-[#00A3FF]/30 text-[#00A3FF] text-xs font-semibold">
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ===========================  SECTION 10: Best of Best  =========================== */
function BestOfBestAdvantage() {
  const list = [
    "Your verified LocalBest page",
    "Your Best of Best status",
    "Your offers and rewards",
    "Your AI assistant",
    "Your business story",
    "Your videos and photos",
    "Your booking links",
    "Your reviews and trust signals",
    "Your local dispatch position",
  ];
  return (
    <Section id="best-of-best" tone="darker">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <SectionHeader
            eyebrow="Powered by Best of Best"
            title="Powered by the LocalBest.ai Best of Best Ecosystem"
            align="left"
          />
          <p className="text-[#C7D2E0] mt-4 leading-relaxed">
            This postcard is even more powerful when paired with a Best of Best listing. Your campaign can promote:
          </p>
          <ul className="grid sm:grid-cols-2 gap-2.5 mt-5">
            {list.map((l, i) => (
              <li key={i} className="flex items-start gap-2 text-[#E0E7F0]">
                <CheckCircle2 className="w-4 h-4 text-[#F4B41A] mt-1 flex-shrink-0" />
                {l}
              </li>
            ))}
          </ul>
          <div className="mt-8 p-6 rounded-2xl border-l-4 border-[#F4B41A] bg-[#F4B41A]/[0.06]">
            <p className="text-white leading-relaxed">
              <strong>The postcard</strong> gets attention.<br />
              <strong>The listing</strong> builds trust.<br />
              <strong>The AI</strong> answers questions.<br />
              <strong>The CRM</strong> captures the lead.<br />
              <strong>The retargeting</strong> keeps you visible.<br />
              <strong>The follow-up</strong> moves the customer forward.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to="/best-of-best">
              <Button variant="outline" className="border-[#F4B41A]/40 bg-[#F4B41A]/10 hover:bg-[#F4B41A]/20 text-[#F4B41A] rounded-full">
                <Crown className="w-4 h-4 mr-2" /> Best of Best Listing
              </Button>
            </Link>
            <Link to="/onlybest">
              <Button variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full">
                <Trophy className="w-4 h-4 mr-2" /> Join OnlyBest
              </Button>
            </Link>
            <Link to="/ai-heroes">
              <Button variant="outline" className="border-[#00A3FF]/40 bg-[#00A3FF]/10 hover:bg-[#00A3FF]/20 text-[#00A3FF] rounded-full">
                <Bot className="w-4 h-4 mr-2" /> AI Business Heroes
              </Button>
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_20px_60px_-20px_rgba(0,163,255,0.35)]">
            <img src={ECOSYSTEM_IMG} alt="AI Horse-Powered Systems — Best of the Best Partner Kit" className="w-full h-auto" />
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ===========================  SECTION 12: Why Now  =========================== */
function WhyNow() {
  return (
    <Section id="why-now" tone="darker">
      <SectionHeader
        eyebrow="Why act now"
        title="Your Competitors Are Still Mailing Paper. You Can Mail an AI-Powered Sales Experience."
      />
      <div className="max-w-3xl mx-auto text-[#C7D2E0] leading-relaxed text-center mt-4 space-y-5">
        <p>
          Most local businesses are still using basic postcards, basic ads, and basic follow-up.
        </p>
        <p>
          The Postcard That Talks Back™ gives your business a more memorable way to reach people,
          educate them, offer value, and stay in front of them after the first impression.
        </p>
        <p className="text-white font-semibold text-lg">
          This is the kind of campaign that makes people stop, scan, ask, call, and remember.
        </p>
      </div>
    </Section>
  );
}

/* ===========================  SECTION 13: Final CTA  =========================== */
function FinalCTA() {
  return (
    <section className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] bg-[#F4B41A]/15 blur-[140px] rounded-full" />
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] tracking-tight">
            Ready to Make Your{" "}
            <span className="bg-gradient-to-r from-[#F4B41A] to-[#FFD66B] bg-clip-text text-transparent">
              Postcard Talk Back?
            </span>
          </h2>
          <p className="mt-6 text-lg text-[#C7D2E0] max-w-2xl mx-auto leading-relaxed">
            Let LocalBest.ai design the postcard, target the right audience, connect the QR code,
            activate the AI assistant, add offers, capture leads, and follow up everywhere your
            customers spend time.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <a href="#inquiry-form">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-gradient-to-r from-[#F4B41A] to-[#E0A015] hover:from-[#FFD66B] hover:to-[#F4B41A] text-[#0A0F1C] font-bold rounded-full px-7 shadow-[0_0_30px_rgba(244,180,26,0.4)]"
                data-testid="final-cta-start"
              >
                Start My Talking Postcard Campaign
              </Button>
            </a>
            <a href="#inquiry-form">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full px-7"
                data-testid="final-cta-demo"
              >
                Request a Demo
              </Button>
            </a>
            <Link to="/submit">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-[#00A3FF]/40 bg-[#00A3FF]/10 hover:bg-[#00A3FF]/20 text-[#00A3FF] rounded-full px-7"
                data-testid="final-cta-list"
              >
                Get Featured on LocalBest.ai
              </Button>
            </Link>
          </div>
          <p className="mt-10 text-xs text-[#64748B] italic max-w-2xl mx-auto">
            The Postcard That Talks Back™ is powered by LocalBest.ai, verified listings, AI
            assistants, AI Voice Agents, direct mail, CRM automation, and omnichannel retargeting.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ===========================  SECTION: Inquiry Form  =========================== */
function InquiryForm() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", business_name: "", phone: "", email: "",
    website: "", business_category: "", target_market: "",
    has_localbest_listing: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim()) {
      toast.error("Please add at least your first name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/marketing/postcard-inquiry`, form);
      setSubmitted(true);
      toast.success("Request received! We'll be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="inquiry-form" className="relative py-20 bg-[#02060F] border-t border-white/5">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="Request your custom quote"
          title="Request My AI Postcard Campaign"
          subtitle="Tell us about your business and we'll send a custom-quote campaign plan within 1 business day."
        />

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-10 rounded-2xl p-10 border border-[#F4B41A]/40 bg-gradient-to-br from-[#F4B41A]/[0.08] to-transparent text-center"
            data-testid="form-success"
          >
            <CheckCircle2 className="w-14 h-14 text-[#F4B41A] mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">You're on the list!</h3>
            <p className="text-[#C7D2E0]">
              We'll be in touch within 1 business day to design your AI postcard campaign.
            </p>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="mt-10 grid sm:grid-cols-2 gap-4" data-testid="inquiry-form">
            <Field label="First Name *" testid="first-name">
              <Input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} required className="bg-white/5 border-white/10 text-white" />
            </Field>
            <Field label="Last Name" testid="last-name">
              <Input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} className="bg-white/5 border-white/10 text-white" />
            </Field>
            <Field label="Business Name" testid="business-name" full>
              <Input value={form.business_name} onChange={(e) => update("business_name", e.target.value)} className="bg-white/5 border-white/10 text-white" />
            </Field>
            <Field label="Phone" testid="phone">
              <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} type="tel" className="bg-white/5 border-white/10 text-white" />
            </Field>
            <Field label="Email *" testid="email">
              <Input value={form.email} onChange={(e) => update("email", e.target.value)} type="email" required className="bg-white/5 border-white/10 text-white" />
            </Field>
            <Field label="Website" testid="website" full>
              <Input value={form.website} onChange={(e) => update("website", e.target.value)} placeholder="https://" className="bg-white/5 border-white/10 text-white" />
            </Field>
            <Field label="Business Category" testid="category">
              <Input value={form.business_category} onChange={(e) => update("business_category", e.target.value)} placeholder="e.g. Mortgage, Roofing, Real Estate" className="bg-white/5 border-white/10 text-white" />
            </Field>
            <Field label="Target City / Market" testid="market">
              <Input value={form.target_market} onChange={(e) => update("target_market", e.target.value)} placeholder="e.g. Tampa FL, Dawsonville GA" className="bg-white/5 border-white/10 text-white" />
            </Field>
            <Field label="Do you already have a LocalBest listing?" testid="has-listing">
              <select
                value={form.has_localbest_listing}
                onChange={(e) => update("has_localbest_listing", e.target.value)}
                className="w-full h-10 px-3 rounded-md bg-[#0F172A] border border-white/15 text-white text-sm focus:outline-none focus:border-[#F4B41A]/60"
                style={{ colorScheme: "dark" }}
              >
                <option value="" className="bg-[#0F172A] text-white">Select…</option>
                <option value="Yes" className="bg-[#0F172A] text-white">Yes</option>
                <option value="No, but I want one" className="bg-[#0F172A] text-white">No, but I want one</option>
                <option value="Not sure" className="bg-[#0F172A] text-white">Not sure</option>
              </select>
            </Field>
            <Field label="Message" testid="message" full>
              <Textarea
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={4}
                placeholder="Tell us about your offer, audience, or goals."
                className="bg-white/5 border-white/10 text-white"
              />
            </Field>
            <div className="sm:col-span-2 mt-2">
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-[#F4B41A] to-[#E0A015] hover:from-[#FFD66B] hover:to-[#F4B41A] text-[#0A0F1C] font-bold rounded-full py-6 text-base shadow-[0_0_30px_rgba(244,180,26,0.35)]"
                data-testid="inquiry-submit"
              >
                {submitting ? "Sending…" : "Request My AI Postcard Campaign"}
              </Button>
              <p className="text-xs text-[#64748B] text-center mt-3">
                We respect your inbox. No spam, ever.
              </p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function Field({ label, children, testid, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""} data-testid={`field-${testid}`}>
      <label className="block text-sm text-[#94A3B8] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

/* ===========================  Shared building blocks  =========================== */
function Section({ id, children, tone }) {
  return (
    <section
      id={id}
      className={`relative py-20 lg:py-24 ${
        tone === "darker" ? "bg-[#02060F]" : ""
      } border-t border-white/5`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
    </section>
  );
}

function SectionHeader({ eyebrow, title, subtitle, align = "center" }) {
  return (
    <div className={align === "center" ? "text-center max-w-4xl mx-auto" : "max-w-3xl"}>
      {eyebrow && (
        <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#F4B41A] font-semibold mb-3">
          {eyebrow}
        </p>
      )}
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] text-white tracking-tight leading-[1.1]">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-[#94A3B8] max-w-2xl mx-auto leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}
